import { Controller, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { User } from "@/api/people";
import type { Position } from "@/api/positions";
import type { Calendar } from "@/api/templates";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DateField } from "@/components/ui/date-field";
import { formControlChromeClassName } from "@/components/ui/form-control";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FormFieldError, type FormValidationIssue } from "@/components/ui/form-validation";
import { useCreatePosition, usePositions } from "@/hooks/usePositions";
import { useCalendars } from "@/hooks/useTemplates";
import { useUnits } from "@/hooks/useUnits";
import {
  OFFICE_ROLES,
  type OfficeRole,
  type PersonFormValues,
  type RoleGroup,
} from "@/components/people/personFormModel";
import { collectPersonValidationIssues } from "@/components/people/personValidationIssues";

interface Props {
  mode: "create" | "edit";
  form: UseFormReturn<PersonFormValues>;
  user?: User;
  currentUserId?: number | null;
  activeAdminsCount?: number;
  allowedRoles?: string[];
  lockedUnitId?: number | null;
  initialUnitId?: number | null;
  canManageProjectManager?: boolean;
  validationIssues?: FormValidationIssue[];
}

const PERSON_FORM_CONTROL_CLASS = "w-full px-2.5 py-1 text-sm font-normal";
const PERSON_FORM_INPUT_CLASS = "text-sm font-normal";

export function PersonFormFields({
  mode,
  form,
  user,
  currentUserId = null,
  activeAdminsCount = 0,
  allowedRoles,
  lockedUnitId = null,
  initialUnitId = null,
  canManageProjectManager = false,
  validationIssues,
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const unitsQuery = useUnits();
  const calendarsQuery = useCalendars();
  const positionsQuery = usePositions(true);
  const createPositionMut = useCreatePosition();
  const isCreate = mode === "create";
  const roleOptions = allowedRoles ?? ["admin", "viewer", "office_staff", "worker"];
  const officeRolesAllowed = OFFICE_ROLES.filter((role) => roleOptions.includes(role));
  const canPickOffice = officeRolesAllowed.length > 0;
  const canPickWorker = roleOptions.includes("worker");
  const availableGroups: RoleGroup[] = [
    ...(canPickOffice ? (["office"] as RoleGroup[]) : []),
    ...(canPickWorker ? (["worker"] as RoleGroup[]) : []),
  ];
  const showGroupSwitcher = isCreate && availableGroups.length > 1;
  const unitLocked = lockedUnitId != null;
  const group = form.watch("group") as RoleGroup;
  const officeRole = group === "office" ? (form.watch("role") as OfficeRole) : "viewer";
  const unitId = form.watch("unit_id");
  const calendarId = form.watch("calendar_id") as number | null | undefined;
  const positionId = form.watch("position_id") as number | null | undefined;
  const isProjectManager = form.watch("is_project_manager");
  const errors = form.formState.errors;
  const isSelf = currentUserId !== null && user?.id === currentUserId;
  const targetIsActiveAdmin = user?.role === "admin" && user.is_active;
  const lockNonAdminRole =
    (isSelf && user?.role === "admin") || (targetIsActiveAdmin && activeAdminsCount <= 1);
  const unitDisplayName = unitId
    ? ((unitsQuery.data ?? []).find((unit) => unit.id === unitId)?.name ?? String(unitId))
    : t("admin.users.unitNone");

  function switchGroup(next: RoleGroup): void {
    if (next === "office") {
      form.reset({
        group: "office",
        last_name: user?.last_name ?? "",
        first_name: user?.first_name ?? "",
        patronymic: user?.patronymic ?? "",
        role: user?.role && user.role !== "worker" ? (user.role as OfficeRole) : "office_staff",
        unit_id: user?.unit_id ?? lockedUnitId ?? initialUnitId ?? null,
        calendar_id: user?.calendar_id ?? null,
        web_email: user?.web_email ?? "",
        tab_number: user?.tab_number ?? "",
        position_id: user?.position_id ?? null,
        tracks_time: user?.tracks_time ?? false,
        is_project_manager: user?.is_project_manager ?? false,
        hire_date: user?.hire_date ?? null,
        termination_date: user?.termination_date ?? null,
      });
      return;
    }
    if (isProjectManager) {
      form.setError("is_project_manager", { type: "manual", message: "projectManagerRole" });
      return;
    }
    form.reset({
      group: "worker",
      last_name: "",
      first_name: "",
      patronymic: "",
      tab_number: "",
      pin: "",
      unit_id: lockedUnitId ?? initialUnitId ?? 0,
      calendar_id: null,
      position_id: null,
      hire_date: null,
      termination_date: null,
    });
  }

  async function createAndPickPosition(name: string): Promise<void> {
    try {
      const created = await createPositionMut.mutateAsync({ name, is_active: true, sort_order: 0 });
      if (created.id != null) {
        form.setValue("position_id", created.id, { shouldDirty: true, shouldValidate: true });
      }
    } catch (error) {
      showApiErrorToast(error, { fallbackMessage: t("common.apiError.fallback") });
    }
  }

  function issue(_name: string, controlId: string): FormValidationIssue | undefined {
    const allIssues =
      validationIssues ??
      collectPersonValidationIssues(
        errors as Record<string, { message?: unknown } | undefined>,
        group,
        t,
      );
    return allIssues.find((candidate) => candidate.controlId === controlId);
  }

  function updateEmploymentDate(
    fieldName: "hire_date" | "termination_date",
    value: string | null,
  ): void {
    form.setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    void form.trigger(["hire_date", "termination_date"]);
  }

  const tracksTimeToggle = !isCreate ? (
    <label className="flex items-center gap-2 text-sm">
      <Controller
        control={form.control}
        name="tracks_time"
        render={({ field }) => (
          <Switch
            data-testid="user-field-tracks-time"
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
      {t("admin.users.fields.tracksTime")}
    </label>
  ) : null;
  const projectManagerToggle =
    canManageProjectManager && group === "office" ? (
      <div>
        <label className="flex items-center gap-2 text-sm">
          <Controller
            control={form.control}
            name="is_project_manager"
            render={({ field }) => (
              <Switch
                id="user-field-project-manager"
                data-testid="user-field-project-manager"
                checked={field.value}
                aria-invalid={Boolean("is_project_manager" in errors && errors.is_project_manager)}
                aria-describedby={
                  "is_project_manager" in errors && errors.is_project_manager
                    ? "user-field-project-manager-error"
                    : undefined
                }
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  if (checked) void form.trigger("is_project_manager");
                  else form.clearErrors("is_project_manager");
                }}
              />
            )}
          />
          {t("admin.users.fields.projectManager")}
        </label>
        <FormFieldError
          issue={
            "is_project_manager" in errors && errors.is_project_manager
              ? {
                  id: "user-field-project-manager-error",
                  message: t("admin.users.errors.projectManagerRole"),
                  kind: "field",
                  controlId: "user-field-project-manager",
                }
              : undefined
          }
        />
      </div>
    ) : null;

  const positionOptions = (positionsQuery.data ?? [])
    .filter(
      (position: Position) =>
        position.id != null && (position.is_active || position.id === positionId),
    )
    .map((position: Position) => ({ value: String(position.id), label: position.name }));
  const positionField = (
    <div className="text-sm">
      <span className="text-muted-foreground mb-1 block text-xs">
        {t("admin.users.fields.position")}
      </span>
      <Controller
        control={form.control}
        name="position_id"
        render={({ field }) => (
          <Combobox
            options={positionOptions}
            value={field.value == null ? null : String(field.value)}
            onChange={(next) => field.onChange(next === null ? null : Number(next))}
            placeholder={t("admin.users.fields.positionEmptyValue")}
            searchPlaceholder={t("admin.users.fields.positionSearchPlaceholder")}
            emptyText={t("admin.users.fields.positionNotFound")}
            clearLabel={t("admin.users.fields.positionClear")}
            onCreate={(name) => void createAndPickPosition(name)}
            createLabel={(name) => t("admin.users.fields.positionCreate", { name })}
            className={PERSON_FORM_CONTROL_CLASS}
            data-testid="user-field-position"
          />
        )}
      />
    </div>
  );
  const employmentDateFields = (
    <>
      {(["hire_date", "termination_date"] as const).map((fieldName) => (
        <div key={fieldName} className="text-sm">
          <span className="text-muted-foreground mb-1 block text-xs">
            {t(`admin.users.fields.${fieldName === "hire_date" ? "hireDate" : "terminationDate"}`)}
          </span>
          <Controller
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <DateField
                value={field.value}
                onChange={(value) => updateEmploymentDate(fieldName, value)}
                label={t(
                  `admin.users.fields.${fieldName === "hire_date" ? "hireDate" : "terminationDate"}`,
                )}
                name={field.name}
                id={`user-field-${fieldName.replace("_", "-")}`}
                aria-invalid={Boolean(errors[fieldName])}
                aria-describedby={
                  errors[fieldName] ? `user-field-${fieldName.replace("_", "-")}-error` : undefined
                }
                className="w-full [&>button]:w-full"
                triggerClassName={PERSON_FORM_CONTROL_CLASS}
                data-testid={`user-field-${fieldName.replace("_", "-")}`}
              />
            )}
          />
          <FormFieldError issue={issue(fieldName, `user-field-${fieldName.replace("_", "-")}`)} />
        </div>
      ))}
    </>
  );

  return (
    <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
      {showGroupSwitcher && (
        <div className="text-sm">
          <label htmlFor="user-group" className="text-muted-foreground mb-1 block text-xs">
            {t("admin.users.groupSwitcher.label")}
          </label>
          <Select
            id="user-group"
            data-testid="user-group"
            value={group}
            onChange={(value) => switchGroup(value as RoleGroup)}
            placeholder={t("admin.users.groupSwitcher.label")}
            options={availableGroups.map((value) => ({
              value,
              label: t(`admin.users.groupSwitcher.${value}`),
            }))}
            className={PERSON_FORM_CONTROL_CLASS}
          />
          <FormFieldError issue={issue("group", "user-group")} />
        </div>
      )}

      {(["last_name", "first_name", "patronymic"] as const).map((fieldName) => (
        <label key={fieldName} className="block text-sm">
          <span className="text-muted-foreground mb-1 block text-xs">
            {t(`admin.users.fields.${fieldName}`)}
          </span>
          <Input
            id={`user-field-${fieldName}`}
            data-testid={`user-field-${fieldName}`}
            aria-invalid={Boolean(errors[fieldName])}
            aria-describedby={errors[fieldName] ? `user-field-${fieldName}-error` : undefined}
            className={PERSON_FORM_INPUT_CLASS}
            {...form.register(fieldName)}
          />
          <FormFieldError issue={issue(fieldName, `user-field-${fieldName}`)} />
        </label>
      ))}

      {group === "office" ? (
        <>
          <label className="block text-sm sm:col-span-2">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("admin.users.fields.email")}
            </span>
            <Input
              id="user-field-email"
              type="email"
              data-testid="user-field-email"
              disabled={!isCreate}
              aria-invalid={Boolean("web_email" in errors && errors.web_email)}
              aria-describedby={
                "web_email" in errors && errors.web_email ? "user-field-email-error" : undefined
              }
              className={PERSON_FORM_INPUT_CLASS}
              {...form.register("web_email")}
            />
            <FormFieldError issue={issue("web_email", "user-field-email")} />
          </label>

          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("admin.users.fields.tabNumberOptional")}
            </span>
            <Input
              id="user-field-office-tab"
              data-testid="user-field-office-tab"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              placeholder={t("admin.users.fields.tabNumberPlaceholder")}
              aria-invalid={Boolean(errors.tab_number)}
              aria-describedby={errors.tab_number ? "user-field-office-tab-error" : undefined}
              className={PERSON_FORM_INPUT_CLASS}
              {...form.register("tab_number")}
            />
            <FormFieldError issue={issue("tab_number", "user-field-office-tab")} />
          </label>

          <div className="text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("admin.users.fields.role")}
            </span>
            {officeRolesAllowed.length <= 1 ? (
              <Button
                type="button"
                variant="outline"
                size="default"
                className={`${formControlChromeClassName} ${PERSON_FORM_CONTROL_CLASS} justify-start`}
                disabled
                data-testid="user-field-role-fixed"
              >
                <span className="min-w-0 truncate">{t(`role.${officeRole}`)}</span>
              </Button>
            ) : (
              <Select
                data-testid="user-field-role-trigger"
                value={officeRole}
                onChange={(value) => {
                  form.setValue("role", value as OfficeRole, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  void form.trigger("is_project_manager");
                }}
                placeholder={t("admin.users.fields.role")}
                options={officeRolesAllowed.map((value) => ({
                  value,
                  label: t(`role.${value}`),
                  disabled:
                    (value !== "admin" && lockNonAdminRole) ||
                    (!canManageProjectManager && isProjectManager && value === "viewer"),
                }))}
                className={PERSON_FORM_CONTROL_CLASS}
              />
            )}
          </div>

          <UnitField
            unitId={unitId}
            unitLocked={unitLocked}
            unitDisplayName={unitDisplayName}
            units={unitsQuery.data ?? []}
            onChange={(value) =>
              form.setValue("unit_id", value == null ? null : Number(value), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            error={issue("unit_id", "user-field-unit-trigger")}
          />
          {positionField}
          {employmentDateFields}
          <div className="sm:col-span-2">
            <ScheduleField
              calendarId={calendarId ?? null}
              calendars={calendarsQuery.data ?? []}
              onSelect={(id) =>
                form.setValue("calendar_id", id, { shouldDirty: true, shouldValidate: true })
              }
            />
          </div>
          {tracksTimeToggle && <div>{tracksTimeToggle}</div>}
          {projectManagerToggle && <div>{projectManagerToggle}</div>}
        </>
      ) : (
        <>
          <label className="block text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              {t("admin.users.fields.tabNumber")}
            </span>
            <Input
              id="user-field-tab"
              data-testid="user-field-tab"
              inputMode="numeric"
              pattern="\d*"
              maxLength={6}
              placeholder="000"
              aria-invalid={Boolean(errors.tab_number)}
              aria-describedby={errors.tab_number ? "user-field-tab-error" : undefined}
              className={PERSON_FORM_INPUT_CLASS}
              {...form.register("tab_number")}
            />
            <FormFieldError issue={issue("tab_number", "user-field-tab")} />
          </label>
          {isCreate && (
            <label className="block text-sm">
              <span className="text-muted-foreground mb-1 block text-xs">
                {t("admin.users.fields.initialPin")}
              </span>
              <Input
                id="user-field-pin"
                data-testid="user-field-pin"
                inputMode="numeric"
                pattern="\d*"
                maxLength={4}
                placeholder="0000"
                aria-invalid={Boolean("pin" in errors && errors.pin)}
                aria-describedby={
                  "pin" in errors && errors.pin ? "user-field-pin-error" : undefined
                }
                className={PERSON_FORM_INPUT_CLASS}
                {...form.register("pin")}
              />
              <FormFieldError issue={issue("pin", "user-field-pin")} />
            </label>
          )}
          <UnitField
            unitId={unitId}
            unitLocked={unitLocked}
            unitDisplayName={unitDisplayName}
            units={unitsQuery.data ?? []}
            onChange={(value) =>
              form.setValue("unit_id", value == null ? 0 : Number(value), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            error={issue("unit_id", "user-field-unit-trigger")}
          />
          {positionField}
          {employmentDateFields}
          <div className="sm:col-span-2">
            <ScheduleField
              calendarId={calendarId ?? null}
              calendars={calendarsQuery.data ?? []}
              onSelect={(id) =>
                form.setValue("calendar_id", id, { shouldDirty: true, shouldValidate: true })
              }
            />
          </div>
          {tracksTimeToggle && <div>{tracksTimeToggle}</div>}
        </>
      )}
    </div>
  );
}

interface UnitFieldProps {
  unitId: number | null | undefined;
  unitLocked: boolean;
  unitDisplayName: string;
  units: Array<{ id?: number | null; name: string }>;
  onChange: (value: string | null) => void;
  error?: FormValidationIssue;
}

function UnitField({
  unitId,
  unitLocked,
  unitDisplayName,
  units,
  onChange,
  error,
}: UnitFieldProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="text-sm">
      <span className="text-muted-foreground mb-1 block text-xs">
        {t("admin.users.fields.unit")}
      </span>
      {unitLocked ? (
        <>
          <Button
            type="button"
            variant="outline"
            size="default"
            className={`${formControlChromeClassName} ${PERSON_FORM_CONTROL_CLASS} justify-start`}
            disabled
            data-testid="user-field-unit-locked"
          >
            <span className="min-w-0 truncate">{unitDisplayName}</span>
          </Button>
          <span className="text-muted-foreground mt-1 block text-xs">
            {t("admin.users.fields.unitLockedHint")}
          </span>
        </>
      ) : (
        <Combobox
          data-testid="user-field-unit-trigger"
          id="user-field-unit-trigger"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? error.id : undefined}
          value={unitId ? String(unitId) : null}
          onChange={onChange}
          options={units.flatMap((unit) =>
            unit.id == null ? [] : [{ value: String(unit.id), label: unit.name }],
          )}
          placeholder={t("admin.users.fields.unitPlaceholder")}
          searchPlaceholder={t("common.combobox.searchPlaceholder")}
          emptyText={t("common.combobox.empty")}
          clearLabel={t("admin.users.unitNone")}
          className={PERSON_FORM_CONTROL_CLASS}
        />
      )}
      <FormFieldError issue={error} />
    </div>
  );
}

function ScheduleField({
  calendarId,
  calendars,
  onSelect,
}: {
  calendarId: number | null;
  calendars: Calendar[];
  onSelect: (calendarId: number | null) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const personalCalendars = calendars.filter(
    (calendar) => calendar.id != null && calendar.name !== "default",
  );
  return (
    <div className="text-sm">
      <span className="text-muted-foreground mb-1 block text-xs">
        {t("admin.users.fields.schedule")}
      </span>
      <Select
        data-testid="user-field-schedule-trigger"
        value={calendarId == null ? "default" : String(calendarId)}
        onChange={(value) => onSelect(value === "default" ? null : Number(value))}
        placeholder={t("admin.users.fields.schedule")}
        options={[
          { value: "default", label: t("admin.users.fields.scheduleDefault") },
          ...personalCalendars.map((calendar) => ({
            value: String(calendar.id),
            label: calendar.name,
          })),
        ]}
        className={PERSON_FORM_CONTROL_CLASS}
      />
      <p className="text-muted-foreground mt-1 text-xs">{t("admin.users.fields.scheduleHint")}</p>
    </div>
  );
}
